import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
    validatePayment,
    completePayment,
    PaymentModes,
    TransactionStatus,
    getMessageForErrorCode,
    completeFreePayment,
} from './paymentLogic'
import { usePaystackPayment } from 'react-paystack'
import { useSelector } from 'react-redux'
import { cartSelectors } from './cartSlice'
import notification from '../../components/Notification/notification'
import { userSelectors } from '../../redux/userSlice'
import { PaymentProcessing } from '../../components/Modal/PaymentProcessing'

export const PaymentGatewayStatus = Object.freeze({
    Idle: 'idle',
    Validating: 'validating',
    ValidationSuccess: 'validation-success',
    ValidationError: 'validation-error',
    Paying: 'paying',
    Finalizing: 'finalizing',
    PaymentSuccess: 'payment-success',
    PaymentError: 'payment-error',
    Cancelling: 'cancelling',
    Complete: 'complete',
})

export function usePaymentGateway({
    onSuccessfulPayment,
    onFailedPayment,
    onCancelledPayment,
    onInvalidOrder,
    mode = PaymentModes.Items,
}) {
    const selectedPlan = useSelector(cartSelectors.selectedPlan)
    const itemDetails = useSelector(cartSelectors.cartItemDetails)
    const selectedPlanDetails = itemDetails[selectedPlan?.detailId] ?? {}
    const amount = useSelector(cartSelectors.totalAmount)
    const { email } = useSelector(userSelectors.userDetails)

    const freeCart = useMemo(() => {
        return (
            (mode === PaymentModes.Items && amount === 0) ||
            (mode === PaymentModes.Plan && selectedPlanDetails.amount === 0)
        )
    }, [amount, selectedPlanDetails.amount, mode])

    const [state, setState] = useState({
        status: PaymentGatewayStatus.idle,
        errorCode: null,
        validationToken: null,
        reference: null,
        customer: null,
        paystackOptions: {
            // Paystack expects the amount in kobo
            amount: mode === PaymentModes.Plan ? undefined : amount * 100,
            plan:
                mode === PaymentModes.Plan
                    ? selectedPlanDetails.plan_code
                    : undefined,
            email,
            publicKey: process.env.REACT_APP_PAYSTACK_KEY,
            metadata: {
                token: null,
            },
        },
    })
    const updateState = newState =>
        setState(prevState => ({ ...prevState, ...newState }))

    /*
        We're using a ref here because of closures.
        The validate and pay functions are passed as a pair to the consumer
        of the hook. The result is that the pay function captures the
        initializePayment variable with the original paystackOptions object.
        When validationToken is updated, pay will be updated on the next render,
        but the consumer is expected to call pay immediately after validate,
        and that pay function is essentially "outdated". But by using a ref to
        store the options, we can update the metadata to include the new token,
        and pay will work as expected.
    */

    useEffect(() => {
        updateState({
            paystackOptions: {
                ...state.paystackOptions,
                amount: mode === PaymentModes.Plan ? undefined : amount * 100,
                plan:
                    mode === PaymentModes.Plan
                        ? selectedPlanDetails.plan_code
                        : undefined,
            },
        })
    }, [amount, mode, selectedPlanDetails.plan_code])

    const validate = async () => {
        const [dismiss] = await notification.loading({
            title: 'Preparing your order...',
        })
        updateState({ status: PaymentGatewayStatus.Validating })
        const result = await validatePayment(mode)
        dismiss()
        if (result.success) {
            updateState({
                status: PaymentGatewayStatus.ValidationSuccess,
                validationToken: result.token,
                paystackOptions: {
                    ...state.paystackOptions,
                    reference: result.reference,
                    metadata: {
                        ...state.paystackOptions.metadata,
                        token: result.token,
                    },
                },
            })
            return true
        } else {
            updateState({
                status: PaymentGatewayStatus.ValidationError,
                errorCode: result.errorCode,
            })
            notification.failure(
                result.errorCode === 'NETWORK_ERROR'
                    ? {
                          title: 'Failed to prepare your order',
                          description: getMessageForErrorCode(result.errorCode),
                      }
                    : {
                          title: 'Order rejected',
                          description: `Your order was rejected because: ${getMessageForErrorCode(
                              result.errorCode
                          )}`,
                      }
            )
            if (typeof onInvalidOrder === 'function') {
                onInvalidOrder({
                    errorCode: result.errorCode,
                })
            }
            return false
        }
    }

    const finalize = async ({ reference }) => {
        updateState({
            status: PaymentGatewayStatus.Finalizing,
        })

        let result
        if (freeCart) {
            result = await completeFreePayment({
                token: state.paystackOptions.metadata.token,
                reference,
            })
        } else {
            result = await completePayment({
                token: state.paystackOptions.metadata.token,
                reference,
                status: TransactionStatus.Completed,
            })
        }

        if (result.success) {
            updateState({
                status: PaymentGatewayStatus.PaymentSuccess,
                validationToken: null,
                reference,
                customer: result.customer,
                paystackOptions: {
                    ...state.paystackOptions,
                    metadata: {
                        ...state.paystackOptions.metadata,
                        token: null,
                    },
                },
            })
            if (typeof onSuccessfulPayment === 'function')
                onSuccessfulPayment({
                    reference,
                    earnedPoints: result.earnedPoints,
                    customer: result.customer,
                })
        } else {
            notification.failure(
                result.errorCode === 'NETWORK_ERROR'
                    ? {
                          title: `Failed to complete your order #${reference}`,
                          description: getMessageForErrorCode(result.errorCode),
                          duration: 0,
                      }
                    : {
                          title: 'Order rejected',
                          description: `Your order #${reference} was rejected becasue: ${getMessageForErrorCode(
                              result.errorCode
                          )}.`,
                          duration: 0,
                      }
            )
            updateState({
                status: PaymentGatewayStatus.PaymentError,
                errorCode: result.errorCode,
                reference,
            })
            if (typeof onFailedPayment === 'function')
                onFailedPayment({
                    reference,
                    errorCode: result.errorCode,
                })
        }
    }

    const cancel = async () => {
        updateState({
            status: PaymentGatewayStatus.Cancelling,
        })
        const [dismiss] = await notification.loading({
            title: 'Cancelling your order',
        })
        const result = await completePayment({
            token: state.paystackOptions.metadata.token,
            status: TransactionStatus.Cancelled,
        })
        dismiss()
        if (!result.success) {
            notification.failure({
                title: 'An error occured while cancelling your order',
                description: result.errorCode,
            })
        } else {
            notification.success({
                title: 'Your order has been cancelled',
            })
        }
        updateState({
            status: PaymentGatewayStatus.Idle,
            validationToken: null,
        })
        state.paystackOptions.metadata.token = null
        if (typeof onCancelledPayment === 'function') onCancelledPayment()
    }

    const initializePayment = usePaystackPayment(state.paystackOptions)

    const pay = () => {
        // Validation hasn't occurred, cannot pay
        if (!state.paystackOptions.metadata.token) return

        if (freeCart) {
            return finalize({ reference: state.paystackOptions.reference })
        }

        updateState({ status: PaymentGatewayStatus.Paying })
        // Paystack validaton has a bug that causes it to reject
        // asyn functions, so wrap it up in a nice, normal function
        initializePayment(
            (...args) => finalize(...args),
            (...args) => cancel(...args)
        )
    }

    const processing =
        state.status === PaymentGatewayStatus.Validating ||
        state.status === PaymentGatewayStatus.Paying ||
        state.status === PaymentGatewayStatus.Finalizing

    const renderModal = () => (
        <PaymentProcessing
            open={state.status === PaymentGatewayStatus.Finalizing}
        />
    )

    return {
        ...state,
        validate,
        pay,
        processing,
        renderModal,
    }
}
